import { forwardRef, useState, type InputHTMLAttributes, type Ref, type ReactNode } from 'react';
import { ActionIcon } from '../../core/ActionIcon/ActionIcon.js';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';
import './PasswordInput.css';

export interface PasswordInputProps
  extends InputWrapperBaseProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'type'> {
  /** Toggle visibility button. */
  visibilityToggle?: boolean;
  /** Override visibility-toggle icons. */
  visibilityToggleIcon?: (opts: { reveal: boolean }) => ReactNode;
}

const DefaultEye = ({ reveal }: { reveal: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {reveal ? (
      <>
        <path d="M1 7s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" />
        <circle cx="7" cy="7" r="1.6" />
        <line x1="1" y1="13" x2="13" y2="1" />
      </>
    ) : (
      <>
        <path d="M1 7s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" />
        <circle cx="7" cy="7" r="1.6" />
      </>
    )}
  </svg>
);

export const PasswordInput = forwardRef(function PasswordInput(
  props: PasswordInputProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const {
    label, description, error, required, withAsterisk,
    leftSection, leftSectionWidth, rightSectionWidth,
    size, className, style,
    id, visibilityToggle = true, visibilityToggleIcon,
    ...inputProps
  } = props;
  const [reveal, setReveal] = useState(false);
  const renderIcon = visibilityToggleIcon ?? (({ reveal }) => <DefaultEye reveal={reveal} />);

  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      withAsterisk={withAsterisk}
      leftSection={leftSection}
      rightSection={
        visibilityToggle ? (
          <ActionIcon
            type="button"
            variant="subtle"
            size="sm"
            aria-label={reveal ? 'Hide password' : 'Show password'}
            onClick={() => setReveal((r) => !r)}
            style={{ pointerEvents: 'auto' }}
          >
            {renderIcon({ reveal })}
          </ActionIcon>
        ) : undefined
      }
      leftSectionWidth={leftSectionWidth}
      rightSectionWidth={rightSectionWidth}
      size={size}
      className={className}
      style={style}
      inputId={id}
    >
      {({ inputId, describedBy, invalid, className: ctlClass }) => (
        <input
          ref={ref}
          id={inputId}
          type={reveal ? 'text' : 'password'}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={ctlClass}
          autoComplete={inputProps.autoComplete ?? 'current-password'}
          {...inputProps}
        />
      )}
    </InputWrapper>
  );
});
